import React, { createContext, useContext, useReducer, useState, ReactNode } from "react";
import {
  Exercise as UIExercise,
  ExerciseType,
  MultipleChoiceData,
  isMultipleChoiceData
} from "@/components/exercises/exercise.interfaces";
import {
  exerciseService,
  updateExercisesOrder,
} from "@/services/admin/exercises.service";
import { apiGetAdminExerciseDetail, apiUpdateExerciseForLevelTestGroupAdmin } from "@/services/admin/exercises.api";
import { useAppToast } from "@/hooks/useAppToast";

// Estado
interface ExerciseState {
  exercises: UIExercise[];
  isRefreshing: boolean;
  error: string | null;
}

// Actions
enum ActionType {
  FETCH_EXERCISES_START = 'FETCH_EXERCISES_START',
  FETCH_EXERCISES_SUCCESS = 'FETCH_EXERCISES_SUCCESS',
  FETCH_EXERCISES_ERROR = 'FETCH_EXERCISES_ERROR',
  ADD_EXERCISE = 'ADD_EXERCISE',
  UPDATE_EXERCISE = 'UPDATE_EXERCISE',
  DELETE_EXERCISE = 'DELETE_EXERCISE',
  SET_EXERCISES = 'SET_EXERCISES',
  SORT_UPDATE_START = 'SORT_UPDATE_START',
  SORT_UPDATE_SUCCESS = 'SORT_UPDATE_SUCCESS',
  SORT_UPDATE_ERROR = 'SORT_UPDATE_ERROR'
}

type ExerciseAction =
  | { type: ActionType.FETCH_EXERCISES_START }
  | { type: ActionType.FETCH_EXERCISES_SUCCESS; payload: UIExercise[] }
  | { type: ActionType.FETCH_EXERCISES_ERROR; payload: string }
  | { type: ActionType.ADD_EXERCISE; payload: UIExercise }
  | { type: ActionType.UPDATE_EXERCISE; payload: UIExercise }
  | { type: ActionType.DELETE_EXERCISE; payload: string }
  | { type: ActionType.SET_EXERCISES; payload: UIExercise[] }
  | { type: ActionType.SORT_UPDATE_START }
  | { type: ActionType.SORT_UPDATE_SUCCESS; payload: UIExercise[] }
  | { type: ActionType.SORT_UPDATE_ERROR; payload: string };

// Reducer
const exerciseReducer = (state: ExerciseState, action: ExerciseAction): ExerciseState => {
    switch (action.type) {
        case ActionType.FETCH_EXERCISES_START:
        case ActionType.SORT_UPDATE_START:
          return {
            ...state,
            isRefreshing: true,
            error: null
          };
        case ActionType.FETCH_EXERCISES_SUCCESS:
        case ActionType.SORT_UPDATE_SUCCESS:
          return {
            ...state,
            exercises: action.payload,
            isRefreshing: false,
            error: null
          };
        case ActionType.FETCH_EXERCISES_ERROR:
        case ActionType.SORT_UPDATE_ERROR:
          return {
            ...state,
            isRefreshing: false,
            error: action.payload
          };
        case ActionType.ADD_EXERCISE:
          return {
            ...state,
            exercises: [...state.exercises, action.payload]
          };
        case ActionType.UPDATE_EXERCISE:
          return {
            ...state,
            exercises: state.exercises.map(exercise =>
              exercise.id === action.payload.id ? action.payload : exercise
            )
          };
        case ActionType.DELETE_EXERCISE:
          return {
            ...state,
            exercises: state.exercises.filter(exercise => exercise.id !== action.payload)
          };
        case ActionType.SET_EXERCISES:
          return {
            ...state,
            exercises: action.payload
          };
        default:
          return state;
      }
};

// Context Type
interface LevelTestGroupExerciseContextType {
  levelTestGroupId: string;
  state: ExerciseState;
  refreshExercises: () => Promise<boolean>;
  addExercise: (exercise: UIExercise) => Promise<boolean>;
  updateExercise: (exercise: UIExercise) => Promise<boolean>;
  deleteExercise: (id: string) => Promise<boolean>;
  setExercises: (exercises: UIExercise[]) => void;
  batchUpdateExercisesSortOrder: (newOrder: (string | number)[]) => Promise<boolean>;
  dialogOpen: boolean;
  editingExercise?: UIExercise;
  openDialog: (exercise?: UIExercise) => void;
  closeDialog: () => void;
}

// Context
const LevelTestGroupExerciseContext = createContext<LevelTestGroupExerciseContextType | undefined>(undefined);

export const useLevelTestGroupExerciseContext = () => {
  const context = useContext(LevelTestGroupExerciseContext);
  if (context === undefined) {
    throw new Error("useLevelTestGroupExerciseContext deve ser usado dentro de um LevelTestGroupExerciseProvider");
  }
  return context;
};

// Provider
interface LevelTestGroupExerciseProviderProps {
  children: ReactNode;
  levelTestGroupId: string;
}

export const LevelTestGroupExerciseProvider: React.FC<LevelTestGroupExerciseProviderProps> = ({
  children,
  levelTestGroupId,
}) => {
  const toast = useAppToast();
  const initialState: ExerciseState = {
    exercises: [],
    isRefreshing: false,
    error: null
  };
  const [state, dispatch] = useReducer(exerciseReducer, initialState);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<UIExercise | undefined>();

  const openDialog = async (exercise?: UIExercise) => {
    if (exercise && exercise.id) {
      try {
        // Fetch admin details including AnswerData for level test exercises
        const adminDetail = await apiGetAdminExerciseDetail(levelTestGroupId, String(exercise.id));
        
        // Parse Data and AnswerData
        let parsedData = exercise.data;
        try {
          parsedData = typeof adminDetail.data === 'string' 
            ? JSON.parse(adminDetail.data) 
            : adminDetail.data;
        } catch (e) {
          console.error('Error parsing exercise data:', e);
        }

        // For multiple-choice exercises, build MultipleChoiceData from Data + AnswerData
        if (isMultipleChoiceData(parsedData)) {
          let answerIds: number[] = [];
          try {
            answerIds = typeof adminDetail.answerData === 'string'
              ? JSON.parse(adminDetail.answerData)
              : adminDetail.answerData || [];
          } catch (e) {
            console.error('Error parsing answerData:', e);
          }

          // Build options with isCorrect flags based on AnswerData
          const multipleChoiceData: MultipleChoiceData = {
            question: parsedData.question || "",
            options: parsedData.options.map((opt: any) => {
              const optId = typeof opt.id === 'string' ? parseInt(opt.id) : opt.id;
              return {
                id: String(opt.id),
                text: opt.text || "",
                isCorrect: answerIds.includes(optId),
                order: opt.order
              };
            }),
            multipleCorrectAnswers: parsedData.multiple_answers || parsedData.multipleCorrectAnswers || false
          };

          setEditingExercise({
            ...exercise,
            type: ExerciseType.MultipleChoice,
            data: multipleChoiceData
          } as UIExercise);
        } else {
          // For other exercise types, use the data as-is
          setEditingExercise({ ...exercise, data: parsedData });
        }
      } catch (error) {
        console.error('Error fetching admin exercise details:', error);
        // Fallback to original exercise data if admin fetch fails
        setEditingExercise({ ...exercise });
      }
    } else {
      const defaultMultipleChoiceData: MultipleChoiceData = {
        question: "",
        options: [{ id: "1", text: "", isCorrect: false }],
        multipleCorrectAnswers: false
      };
      setEditingExercise({
        type: ExerciseType.MultipleChoice,
        data: defaultMultipleChoiceData
      } as UIExercise);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingExercise(undefined);
  };

  const refreshExercises = async (): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.FETCH_EXERCISES_START });
      const updatedExercises = await exerciseService.listComponentExercisesByLevelTestGroup(levelTestGroupId);
      dispatch({ type: ActionType.FETCH_EXERCISES_SUCCESS, payload: updatedExercises as UIExercise[] });
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error("Falha ao atualizar exercícios");
      dispatch({ type: ActionType.FETCH_EXERCISES_ERROR, payload: errorMessage });
      return false;
    }
  };

  const addExercise = async (exercise: UIExercise): Promise<boolean> => {
    try {
      const newExercise = await exerciseService.createComponentExerciseForLevelTestGroup(levelTestGroupId, exercise);
      if (newExercise) {
        await refreshExercises();
        toast.success("Exercício adicionado com sucesso!");
        return true;
      }
      return false;
    } catch {
      toast.error("Não foi possível adicionar o exercício.");
      return false;
    }
  };

  const updateExercise = async (exercise: UIExercise): Promise<boolean> => {
    if (!exercise.id) return false;
    try {
      const exerciseId = typeof exercise.id === 'string' ? exercise.id : String(exercise.id);
      
      // Optimistic update - update local state immediately for instant feedback
      dispatch({ type: ActionType.UPDATE_EXERCISE, payload: exercise });
      
      // For multiple-choice exercises, use admin API with AnswerData support
      if (isMultipleChoiceData(exercise.data)) {
        const mcData = exercise.data;
        
        // Extract correct answer IDs from options
        const correctAnswerIds = mcData.options
          .filter(opt => opt.isCorrect)
          .map(opt => {
            const id = typeof opt.id === 'string' ? parseInt(opt.id) : opt.id;
            return typeof id === 'string' ? parseInt(id) : id;
          })
          .filter(id => !isNaN(id));

        // Build Data without isCorrect flags
        const dataWithoutCorrectness = {
          question: mcData.question,
          options: mcData.options.map(opt => ({
            id: typeof opt.id === 'string' ? parseInt(opt.id) : opt.id,
            text: opt.text,
          })),
          multiple_answers: mcData.multipleCorrectAnswers || false
        };

        // Generate title from question
        const title = mcData.question?.substring(0, 200) || 'Exercise';

        // Use admin API endpoint
        await apiUpdateExerciseForLevelTestGroupAdmin(levelTestGroupId, exerciseId, {
          title,
          description: title,
          type: 'MultipleChoice',
          data: JSON.stringify(dataWithoutCorrectness),
          answerData: JSON.stringify(correctAnswerIds)
        });

        // Refresh in background to get the actual server state
        setTimeout(async () => {
          await refreshExercises();
        }, 500);
        
        toast.success("Exercício atualizado com sucesso!");
        return true;
      } else {
        // For non-multiple-choice exercises, use the regular update
        const updatedExercise = await exerciseService.updateComponentExerciseForLevelTestGroup(exerciseId, exercise, levelTestGroupId);
        if (updatedExercise) {
          setTimeout(async () => {
            await refreshExercises();
          }, 500);
          
          toast.success("Exercício atualizado com sucesso!");
          return true;
        } else {
          await refreshExercises();
          return false;
        }
      }
    } catch (error) {
      console.error('Error updating exercise:', error);
      // If update failed, revert the optimistic update
      await refreshExercises();
      toast.error("Não foi possível atualizar o exercício.");
      return false;
    }
  };

  const deleteExercise = async (id: string): Promise<boolean> => {
    try {
      await exerciseService.deleteComponentExerciseForLevelTestGroup(id, levelTestGroupId);
      await refreshExercises();
      toast.success("Exercício excluído com sucesso!");
      return true;
    } catch {
      toast.error("Não foi possível excluir o exercício.");
      return false;
    }
  };

  const setExercises = (exercises: UIExercise[]) => {
    dispatch({ type: ActionType.SET_EXERCISES, payload: exercises });
  };

  const batchUpdateExercisesSortOrder = async (newOrder: (string | number)[]): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.SORT_UPDATE_START });
      const stringOrder = newOrder.map(String);
      await updateExercisesOrder(levelTestGroupId, stringOrder);
      await refreshExercises();
      toast.success("Ordem dos exercícios atualizada!");
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro";
      dispatch({ type: ActionType.SORT_UPDATE_ERROR, payload: errorMessage });
      return false;
    }
  };

  const contextValue: LevelTestGroupExerciseContextType = {
    levelTestGroupId,
    state,
    refreshExercises,
    addExercise,
    updateExercise,
    deleteExercise,
    setExercises,
    batchUpdateExercisesSortOrder,
    dialogOpen,
    editingExercise,
    openDialog,
    closeDialog
  };

  return (
    <LevelTestGroupExerciseContext.Provider value={contextValue}>
      {children}
    </LevelTestGroupExerciseContext.Provider>
  );
};

